import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useTeamSlug } from '@/context/TeamSlugContext'
import { useTranslation } from '@/hooks/useTranslation'
import { loadConsentedThirdPartyScripts } from '@/lib/thirdPartyScripts'

export const COOKIE_CONSENT_KEY = 'feedbask-cookie-consent'

type ConsentValue = 'necessary' | 'all'

function readConsent(): ConsentValue | null {
	try {
		const value = localStorage.getItem(COOKIE_CONSENT_KEY)
		if (value === 'necessary' || value === 'all') return value
	} catch {
		return null
	}
	return null
}

export function CookieConsent() {
	const { t } = useTranslation()
	const location = useLocation()
	const { isPortal } = useTeamSlug()
	const [consent, setConsent] = useState<ConsentValue | null>(null)
	const [ready, setReady] = useState(false)

	useEffect(() => {
		const stored = readConsent()
		setConsent(stored)
		setReady(true)
		if (stored === 'all') {
			loadConsentedThirdPartyScripts()
		}
	}, [])

	const hidden =
		isPortal ||
		location.pathname.startsWith('/embed') ||
		location.pathname.startsWith('/auth/callback')

	const choose = (value: ConsentValue) => {
		try {
			localStorage.setItem(COOKIE_CONSENT_KEY, value)
		} catch {
			return
		}
		setConsent(value)
		if (value === 'all') {
			loadConsentedThirdPartyScripts()
		}
	}

	if (!ready || hidden || consent) return null

	return (
		<div className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:w-[380px]">
			<div className="w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-lg">
				<p className="text-sm font-medium text-gray-900">
					{t('cookieBanner.title')}
				</p>
				<p className="mt-2 text-sm text-gray-600">
					{t('cookieBanner.description')}{' '}
					<Link to="/cookies" className="font-medium text-primary hover:text-primary/90">
						{t('cookieBanner.policyLink')}
					</Link>
				</p>
				<div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
					<button
						type="button"
						className="h-10 rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
						onClick={() => choose('necessary')}
					>
						{t('cookieBanner.necessaryOnly')}
					</button>
					<button
						type="button"
						className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
						onClick={() => choose('all')}
					>
						{t('cookieBanner.acceptAll')}
					</button>
				</div>
			</div>
		</div>
	)
}
